<template>
  <Teleport to="body">
    <div ref="sheet" class="sheet" :aria-hidden="!visible" role="dialog">
      <div class="overlay" @click="closeClickOutSite && setIsSheetShown(false)"></div>
      <div ref="sheetContents" class="contents">
        <header v-if="title" class="controls" :class="!enableDrag && 'py-16'">
          <div v-if="enableDrag" ref="draggableArea" class="draggable-area">
            <div class="draggable-thumb"></div>
          </div>
        </header>
        <slot name="header"></slot>
        <slot name="content"></slot>
      </div>
    </div>
  </Teleport>
</template>
<script setup lang="ts">
const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
    required: true,
  },
  title: {
    type: String,
    default: '',
  },
  showClose: {
    type: Boolean,
    default: true,
  },
  heightSheet: {
    type: Array<number>,
    default: [40],
  },
  height: {
    type: [String, Number],
    default: 50,
  },
  enableDrag: {
    type: Boolean,
    default: false,
  },
  closeIcon: {
    type: String,
    default: 'https://s3-cdn.designerjourneys.com/static/icons/icon-close-new.svg',
  },
  closeClickOutSite: {
    type: Boolean,
    default: false,
  },
})
const emits = defineEmits(['close', 'update:visible'])
const { isMobile } = useDevice()
const dragPosition = ref()
const sheetHeight = ref()
const sheet = ref<HTMLElement | null>(null)
const sheetContents = ref<HTMLElement | null>(null)
const draggableArea = ref<HTMLElement | null>(null)
const buttonBottom = ref<HTMLElement | null>(null)
const bottomSheetBody = ref<HTMLElement | null>(null)

onMounted(() => {
  if (props.enableDrag) {
    draggableArea.value!.addEventListener('mousedown', onDragStart)
    draggableArea.value!.addEventListener('touchstart', onDragStart)

    window.addEventListener('mousemove', onDragMove)
    window.addEventListener('touchmove', onDragMove)

    window.addEventListener('mouseup', onDragEnd)
    window.addEventListener('touchend', onDragEnd)
  }

  if (isMobile && buttonBottom.value?.clientHeight) {
    bottomSheetBody.value?.setAttribute('style', `padding-bottom: ${buttonBottom.value?.clientHeight}px;`)
  }
  setSheetHeight(props.height)
  handleDisableScroll()
})

onUnmounted(() => {
  document.body.style.overflow = ''
})

watch(
  () => props.visible,
  () => {
    handleDisableScroll()
  },
)

const handleDisableScroll = () => {
  const count = document.querySelectorAll('.sheet[aria-hidden="false"]').length

  if (props.visible) {
    document.body.style.overflow = 'hidden'
  } else if (count === 1) {
    document.body.style.overflow = ''
  }
}

const touchPosition = (event: MouseEvent | TouchEvent) => {
  if (event instanceof TouchEvent) {
    return event.touches ? event.touches[0] : event
  }
  return event
}
const setSheetHeight = (value: string | number) => {
  const windowHeight = window.innerHeight
  const isNumber = typeof value === 'number'
  const height = isNumber ? value : (Number(value.split('px')[0]) / windowHeight) * 100
  sheetHeight.value = Math.max(0, Math.min(100, height))
  sheetContents.value!.style.height = isNumber ? `${sheetHeight.value}vh` : value

  if (sheetHeight.value === 100) {
    sheetContents.value!.classList.add('fullscreen')
  } else {
    sheetContents.value!.classList.remove('fullscreen')
  }
}
const setIsSheetShown = (isShown: boolean) => {
  if (!isShown) {
    emits('update:visible', false)
    emits('close')
  }
  sheet.value!.setAttribute('aria-hidden', String(!isShown))
}
const onDragStart = (event: MouseEvent | TouchEvent) => {
  dragPosition.value = (touchPosition(event) as MouseEvent).pageY
  sheetContents.value!.classList.add('not-selectable')
  draggableArea.value!.style.cursor = document.body.style.cursor = 'grabbing'
}
const onDragMove = (event: MouseEvent | TouchEvent) => {
  if (dragPosition.value === undefined) return

  const y = (touchPosition(event) as MouseEvent).pageY
  const deltaY = dragPosition.value - y
  const deltaHeight = (deltaY / window.innerHeight) * 100

  setSheetHeight(sheetHeight.value + deltaHeight)
  dragPosition.value = y
}
const onDragEnd = () => {
  dragPosition.value = undefined
  sheetContents.value!.classList.remove('not-selectable')
  draggableArea.value!.style.cursor = document.body.style.cursor = ''

  if (sheetHeight.value < 25) {
    setIsSheetShown(false)
  } else if (sheetHeight.value > 75) {
    setSheetHeight(100)
  } else {
    setSheetHeight(50)
  }
}
</script>
<style lang="scss" scoped>
@import './BottomSheetStyle.scss';
</style>
